import { ViewerContext } from "@/Context/ViewerContext/ViewerContextProvider";
import { useIsMobile } from "@/Hooks/useIsMobile";
import Model from "@/Models/Model";
import { getConfig } from "@/_Configuration/configuration";
import Configurations from "@/components/Configurations/Configurations";
import ContextMenu from "@/components/ContextMenu/ContextMenu";
import IfcViewer from "@/components/IfcViewer/IfcViewer";
import Measurer from "@/components/Measurer/Measurer";
import ModelCombobox from "@/components/ModelComboBox/ModelComboBox";
import Navcube from "@/components/Navcube/Navcube";
import ObjectViews from "@/components/ObjectViews/ObjectViews";
import ObjectViewsButton from "@/components/ObjectViews/ObjectViewsButton";
import OrbitPanToggle from "@/components/OrbitPanToggle/OrbitPanToggle";
import Picker from "@/components/Picker/Picker";
import PropertyWindow from "@/components/PropertyWindow/PropertyWindow";
import PropertyWindowButton from "@/components/PropertyWindow/PropertyWindowButton";
import SectionPlanes from "@/components/SectionPlanes/SectionPlanes";
import SnapshotMaker from "@/components/SnapshotMaker/SnapshotMaker";
import ZoomToFit from "@/components/ZoomToFit/ZoomToFit";
import { ModeToggle } from "@/components/mode-toggle";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import useViewerStore from "@/stores/useViewerStore";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const configuration = {
    id: "viewer-canvas",
    defaults: {
        IfcProject: {
            visible: false,
            pickable: false,
        },
        IfcSpace: {
            colorize: [1, 0.08, 0.58],
            opacity: 0.4,
            pickable: false,
        },
        IfcGrid: {
            colorize: [1, 0.08, 0.58],
            opacity: 0.4,
            pickable: false,
        },
    },
    materials: {
        highlight: {
            fill: true,
            fillColor: [0.94, 0.45, 0.23],
            fillAlpha: 0.3,
            edges: true,
            edgeColor: [0, 0, 0],
            edgeAlpha: 0.5,
            edgeWidth: 0.5,
        },
        xray: {
            fill: true,
            fillColor: [0, 0, 0],
            fillAlpha: 0.25,
            edges: true,
            edgeColor: [0, 0, 0],
            edgeAlpha: 0.2,
            edgeWidth: 1,
        },
        selected: {
            fill: true,
            fillColor: [0.21, 0.39, 0.48],
            fillAlpha: 0.5,
            edges: false,
            edgeColor: [0, 0, 0],
            edgeAlpha: 0,
            edgeWidth: 0,
        },
    },
};

const Viewer = () => {
    const { state } = useContext(ViewerContext);
    const [modelVisual, setModelVisual] = useState<Model[]>([]);
    const isLongPressing = useRef(false);

    const { t } = useTranslation();

    const { modelBrowserOpen, propertiesPanelOpen, setModelBrowserOpen, setPropertiesPanelOpen } = useViewerStore();
    const isMobile = useIsMobile();

    const sidebarOpen = modelBrowserOpen || propertiesPanelOpen;

    const fetchModels = async () => {
        const cdn = getConfig().cdn;
        const models = await fetch(`${cdn}/models.json`);
        const modelData = await models.json();
        setModelVisual(modelData);
    };

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        fetchModels();
    }, []);

    return (
        <main className="fixed w-full h-full">
            <ResizablePanelGroup direction="horizontal" autoSaveId="viewer-horizontal">
                <ResizablePanel id="main-panel" order={0} minSize={50} className="relative">
                    <header className="absolute shadow-md w-full z-50">
                        <nav className="bg-white/50 p-2 dark:bg-gray-800/50 border-b">
                            <div className="flex flex-wrap justify-between items-start mx-auto">
                                <a href="https://dstudio.be" className="flex items-center">
                                    <img
                                        src="https://ifc-cloud-viewer-dev.dstudio.be/logo.svg"
                                        className="mr-3 h-6 sm:h-9"
                                        alt="D-studio Logo"
                                    />
                                    <span className="self-center text-xl font-semibold whitespace-nowrap dark:text-white sm:hidden lg:block">
                                        {t("ifcViewerTitle")}
                                    </span>
                                </a>
                                <div className="flex items-end">
                                    <ModelCombobox models={modelVisual} />
                                    <div className="pl-4">
                                        <ModeToggle />
                                    </div>
                                </div>
                            </div>
                        </nav>
                    </header>
                    <div className="h-full">
                        <IfcViewer configuration={configuration} />
                        <ContextMenu canvasId={configuration.id} isLongPressing={isLongPressing} />
                        <Picker isLongPressing={isLongPressing} />
                        <Navcube useSpacer />

                        <div className="absolute bottom-[50px] left-1/2 transform -translate-x-1/2  flex">
                            <div className="flex p-1 border border-gray-200 dark:border-slate-600 bg-slate rounded-md bg-white dark:bg-slate-950">
                                <OrbitPanToggle />
                                <ZoomToFit />
                            </div>
                            <div className="flex ml-2 p-1 border border-gray-200 dark:border-slate-600 bg-slate rounded-md items-center bg-white dark:bg-slate-950">
                                {state.openPlugin != "SectionPlanes" && <Measurer />}
                                {state.openPlugin != "Measurer" && <SectionPlanes />}
                            </div>
                            <div className="flex ml-2 p-1 border border-gray-200 dark:border-slate-600 bg-slate rounded-md bg-white dark:bg-slate-950">
                                <ObjectViewsButton />
                                <PropertyWindowButton />
                                <SnapshotMaker />
                                <Configurations />
                            </div>
                        </div>
                    </div>
                </ResizablePanel>

                {!isMobile && sidebarOpen && (
                    <>
                        <ResizableHandle withHandle />

                        <ResizablePanel id="side-panel" order={1} minSize={25} className="m-0 z-50">
                            <ResizablePanelGroup direction="vertical" autoSaveId="viewer-vertical">
                                {modelBrowserOpen && (
                                    <ResizablePanel id="object-views-panel" order={0} defaultSize={50} minSize={25}>
                                        <ObjectViews />
                                    </ResizablePanel>
                                )}

                                {modelBrowserOpen && propertiesPanelOpen && <ResizableHandle withHandle />}

                                {propertiesPanelOpen && (
                                    <ResizablePanel id="property-window-panel" order={1} defaultSize={50} minSize={25}>
                                        <PropertyWindow />
                                    </ResizablePanel>
                                )}
                            </ResizablePanelGroup>
                        </ResizablePanel>
                    </>
                )}
            </ResizablePanelGroup>

            {isMobile && (
                <Drawer
                    open={sidebarOpen}
                    onOpenChange={(open) => {
                        if (!open) {
                            setModelBrowserOpen(false);
                            setPropertiesPanelOpen(false);
                        }
                    }}
                >
                    <VisuallyHidden>
                        <DrawerTitle>{modelBrowserOpen ? "Objects" : "Properties"}</DrawerTitle>
                    </VisuallyHidden>
                    <DrawerContent className="h-[60vh]" aria-describedby={undefined}>
                        <Tabs defaultValue={modelBrowserOpen ? "objects" : "properties"}>
                            <TabsContent value="objects" className="overflow-y-auto flex-1">
                                <ObjectViews />
                            </TabsContent>
                            <TabsContent value="properties" className="overflow-y-auto flex-1">
                                <PropertyWindow />
                            </TabsContent>
                        </Tabs>
                    </DrawerContent>
                </Drawer>
            )}
        </main>
    );
};

export default Viewer;
